// Game constants — shared between client and server
// Source of truth: shared/constants.json (values in Hammer Units)
// Conversion: 1 HU ≈ 0.025 m (server-authoritative values, gameplay-tuned)
// These m/s values MUST match server/internal/game/config.go exactly to avoid
// client-prediction divergence (rubber-banding).

export const TICK_RATE = 64;
export const TICK_DURATION_MS = 15.625;
export const MAX_PLAYERS = 10;
export const DEATHMATCH_TIME_S = 600;

// Movement speeds in m/s — must match server config.go
export const MAX_SPEED_KNIFE = 6.5;
export const MAX_SPEED_RIFLE = 5.5;
export const MAX_SPEED_PISTOL = 6.0;
export const MAX_SPEED_SNIPER = 5.0;
export const MAX_SPEED_SMG = 6.0;
export const MAX_SPEED_SHOTGUN = 5.5;
export const CROUCH_SPEED_MULT = 0.34;
export const JUMP_VELOCITY = 6.5;
export const GRAVITY = -20.0;
export const PLAYER_HEIGHT = 1.8;
export const PLAYER_CROUCH_HEIGHT = 1.35;
export const PLAYER_RADIUS = 0.4;
export const AIR_CONTROL = 0.3;

// ── Bhop / Slide Hop — krunker.io-style chained slide-jump rhythm ──
// Must match server config.go exactly
export const BHOP_SPEED_BONUS = 0.08;      // 8% speed increase per successful chain
export const BHOP_MAX_MULTIPLIER = 1.6;    // Max 60% speed bonus from bhop chain
export const BHOP_LANDING_WINDOW = 0.25;   // Seconds after landing to initiate slide for chain
export const BHOP_DECAY_RATE = 8.0;        // How fast bhop speed decays when chain breaks

// ── Slide mechanics — must match server config.go ──
export const SLIDE_SPEED = 8.5;
export const SLIDE_FRICTION = 2.5;
export const SLIDE_DURATION = 0.8;
export const SLIDE_COOLDOWN = 1.2;
export const SLIDE_MIN_SPEED = 3.0;
export const SLIDE_HEIGHT = 0.9;

// ── Tagging (hit slowdown) — must match server config.go + player.go ──
export const TAG_DURATION = 0.4; // seconds of speed reduction after being hit

/** Fraction of speed lost per weapon type when tagged (hit). */
export const TAG_SLOW_AMOUNTS: Record<string, number> = {
  pistol: 0.25,
  smg: 0.30,
  rifle: 0.40,
  sniper: 0.50,
  shotgun: 0.45,
  melee: 0.0,
};

export const MOVEMENT_INACCURACY_MULT = 4.0;
export const CROUCH_ACCURACY_MULT = 0.7;
export const RECOIL_RESET_MS = 400;
export const FIRST_SHOT_ACCURACY = 0.0;

export const HEAD_MULT = 4.0;
export const CHEST_MULT = 1.0;
export const STOMACH_MULT = 1.25;
export const LEGS_MULT = 0.75;
export const ARMOR_ABSORPTION = 0.5;

export const MAX_REWIND_MS = 200;
export const INTERP_TICKS = 2;
export const VISIBILITY_GRACE_MS = 500;

// ── Grenade constants ──
export const GRENADE_GRAVITY = -8.0;
export const GRENADE_BOUNCE = 0.45;
export const GRENADE_OVERHAND_SPEED = 18.75;
export const GRENADE_MEDIUM_SPEED = 14.5;
export const GRENADE_UNDERHAND_SPEED = 10.0;

export const SMOKE_DURATION = 18.0;
export const SMOKE_RADIUS = 3.6;
export const FIRE_DURATION = 7.0;
export const FIRE_RADIUS = 4.75;
export const HE_BLAST_RADIUS = 9.6;
export const FLASH_MAX_BLIND = 3.0;

export const MAX_GRENADES_TOTAL = 4;
export const MAX_FLASHBANGS = 2;

// ── Decoy grenade constants ──
export const DECOY_DURATION = 15.0;          // seconds the decoy stays active
export const DECOY_GUNFIRE_INTERVAL = 500;   // ms between fake gunfire sounds
export const DECOY_EXPLOSION_DAMAGE = 5;     // damage on expiry
export const DECOY_EXPLOSION_RADIUS = 1.0;   // meters

export type GrenadeId = 'he_grenade' | 'smoke_grenade' | 'flashbang' | 'molotov' | 'incendiary' | 'decoy_grenade';

export interface GrenadeConfig {
  name: string;
  price: number;
  team?: 'T' | 'CT'; // undefined = both teams
  maxCarry: number;
  moveSpeed: number;
}

export const GRENADES: Record<GrenadeId, GrenadeConfig> = {
  he_grenade: { name: 'HE Grenade', price: 300, maxCarry: 1, moveSpeed: 6.125 },
  smoke_grenade: { name: 'Smoke Grenade', price: 300, maxCarry: 1, moveSpeed: 6.125 },
  flashbang: { name: 'Flashbang', price: 200, maxCarry: 2, moveSpeed: 6.125 },
  molotov: { name: 'Molotov', price: 400, team: 'T', maxCarry: 1, moveSpeed: 6.125 },
  incendiary: { name: 'Incendiary', price: 500, team: 'CT', maxCarry: 1, moveSpeed: 6.125 },
  decoy_grenade: { name: 'Decoy Grenade', price: 50, maxCarry: 1, moveSpeed: 6.125 },
};

export interface WeaponConfig {
  name: string;
  type: 'pistol' | 'rifle' | 'sniper' | 'melee' | 'smg' | 'shotgun';
  damage: number;
  fireRateRPM: number;
  magazine: number;
  reserveAmmo: number;
  reloadTimeMs: number;
  price: number;
  moveSpeed: number;
  baseSpread: number;
  maxSpread: number;
  spreadPerShot: number;
  killReward: number;
  penetration: number;
  scopeZoom?: number;
  silenced?: boolean;
}

export type WeaponId = 'glock' | 'usp' | 'ak47' | 'm4a4' | 'awp' | 'knife' | 'p250' | 'deagle' | 'famas' | 'galil' | 'mp9' | 'mac10' | 'nova' | 'ssg08' | 'aug' | 'sg553' | 'p90' | 'fiveseven' | 'tec9' | 'm4a1s';

export const WEAPONS: Record<WeaponId, WeaponConfig> = {
  glock: {
    name: 'Glock-18', type: 'pistol', damage: 28, fireRateRPM: 400,
    magazine: 20, reserveAmmo: 120, reloadTimeMs: 2200, price: 200,
    moveSpeed: 6.0, baseSpread: 0.6, maxSpread: 4.0, spreadPerShot: 0.5,
    killReward: 300, penetration: 0.47,
  },
  usp: {
    name: 'USP-S', type: 'pistol', damage: 33, fireRateRPM: 352,
    magazine: 12, reserveAmmo: 24, reloadTimeMs: 2200, price: 200,
    moveSpeed: 6.0, baseSpread: 0.3, maxSpread: 3.5, spreadPerShot: 0.4,
    killReward: 300, penetration: 0.51,
  },
  ak47: {
    name: 'AK-47', type: 'rifle', damage: 36, fireRateRPM: 600,
    magazine: 30, reserveAmmo: 90, reloadTimeMs: 2500, price: 2700,
    moveSpeed: 6.125, baseSpread: 0.2, maxSpread: 5.0, spreadPerShot: 0.35,
    killReward: 300, penetration: 0.77,
  },
  m4a4: {
    name: 'M4A4', type: 'rifle', damage: 33, fireRateRPM: 666,
    magazine: 30, reserveAmmo: 90, reloadTimeMs: 3100, price: 3100,
    moveSpeed: 6.125, baseSpread: 0.2, maxSpread: 4.5, spreadPerShot: 0.3,
    killReward: 300, penetration: 0.70,
  },
  awp: {
    name: 'AWP', type: 'sniper', damage: 115, fireRateRPM: 41,
    magazine: 5, reserveAmmo: 30, reloadTimeMs: 3700, price: 4750,
    moveSpeed: 5.0, baseSpread: 0.1, maxSpread: 6.0, spreadPerShot: 2.0,
    killReward: 100, penetration: 0.97, scopeZoom: 6.0,
  },
  p250: {
    name: 'P250', type: 'pistol', damage: 38, fireRateRPM: 400,
    magazine: 13, reserveAmmo: 26, reloadTimeMs: 2200, price: 300,
    moveSpeed: 6.0, baseSpread: 0.5, maxSpread: 3.8, spreadPerShot: 0.45,
    killReward: 300, penetration: 0.54,
  },
  deagle: {
    name: 'Desert Eagle', type: 'pistol', damage: 63, fireRateRPM: 267,
    magazine: 7, reserveAmmo: 35, reloadTimeMs: 2200, price: 700,
    moveSpeed: 5.75, baseSpread: 0.4, maxSpread: 5.0, spreadPerShot: 0.8,
    killReward: 300, penetration: 0.93,
  },
  mp9: {
    name: 'MP9', type: 'smg', damage: 26, fireRateRPM: 857,
    magazine: 30, reserveAmmo: 120, reloadTimeMs: 2100, price: 1250,
    moveSpeed: 6.0, baseSpread: 0.5, maxSpread: 4.5, spreadPerShot: 0.25,
    killReward: 600, penetration: 0.47,
  },
  mac10: {
    name: 'MAC-10', type: 'smg', damage: 27, fireRateRPM: 800,
    magazine: 30, reserveAmmo: 100, reloadTimeMs: 2200, price: 1050,
    moveSpeed: 6.0, baseSpread: 0.6, maxSpread: 5.0, spreadPerShot: 0.3,
    killReward: 600, penetration: 0.47,
  },
  famas: {
    name: 'FAMAS', type: 'rifle', damage: 30, fireRateRPM: 666,
    magazine: 25, reserveAmmo: 90, reloadTimeMs: 3300, price: 2050,
    moveSpeed: 5.5, baseSpread: 0.3, maxSpread: 5.0, spreadPerShot: 0.35,
    killReward: 300, penetration: 0.70,
  },
  galil: {
    name: 'Galil AR', type: 'rifle', damage: 30, fireRateRPM: 666,
    magazine: 35, reserveAmmo: 90, reloadTimeMs: 3000, price: 1800,
    moveSpeed: 5.375, baseSpread: 0.3, maxSpread: 5.0, spreadPerShot: 0.35,
    killReward: 300, penetration: 0.77,
  },
  nova: {
    name: 'Nova', type: 'shotgun', damage: 26, fireRateRPM: 68,
    magazine: 8, reserveAmmo: 32, reloadTimeMs: 500, price: 1200,
    moveSpeed: 5.5, baseSpread: 2.0, maxSpread: 4.0, spreadPerShot: 0.0,
    killReward: 900, penetration: 0.50,
  },
  ssg08: {
    name: 'SSG 08', type: 'sniper', damage: 88, fireRateRPM: 48,
    magazine: 10, reserveAmmo: 90, reloadTimeMs: 3700, price: 1700,
    moveSpeed: 5.75, baseSpread: 0.1, maxSpread: 5.0, spreadPerShot: 1.5,
    killReward: 300, penetration: 0.85, scopeZoom: 4.0,
  },
  aug: {
    name: 'AUG', type: 'rifle', damage: 28, fireRateRPM: 666,
    magazine: 30, reserveAmmo: 90, reloadTimeMs: 3800, price: 3300,
    moveSpeed: 5.5, baseSpread: 0.2, maxSpread: 4.5, spreadPerShot: 0.3,
    killReward: 300, penetration: 0.90, scopeZoom: 2.5,
  },
  sg553: {
    name: 'SG 553', type: 'rifle', damage: 30, fireRateRPM: 666,
    magazine: 30, reserveAmmo: 90, reloadTimeMs: 2800, price: 3000,
    moveSpeed: 5.25, baseSpread: 0.2, maxSpread: 5.0, spreadPerShot: 0.35,
    killReward: 300, penetration: 1.0, scopeZoom: 2.5,
  },
  p90: {
    name: 'P90', type: 'smg', damage: 26, fireRateRPM: 857,
    magazine: 50, reserveAmmo: 100, reloadTimeMs: 3300, price: 2350,
    moveSpeed: 5.75, baseSpread: 0.5, maxSpread: 5.5, spreadPerShot: 0.2,
    killReward: 300, penetration: 0.57,
  },
  fiveseven: {
    name: 'Five-SeveN', type: 'pistol', damage: 32, fireRateRPM: 400,
    magazine: 20, reserveAmmo: 100, reloadTimeMs: 2200, price: 500,
    moveSpeed: 6.0, baseSpread: 0.5, maxSpread: 3.8, spreadPerShot: 0.45,
    killReward: 300, penetration: 0.91,
  },
  tec9: {
    name: 'Tec-9', type: 'pistol', damage: 33, fireRateRPM: 500,
    magazine: 18, reserveAmmo: 90, reloadTimeMs: 2200, price: 500,
    moveSpeed: 6.0, baseSpread: 0.6, maxSpread: 4.0, spreadPerShot: 0.5,
    killReward: 300, penetration: 0.91,
  },
  m4a1s: {
    name: 'M4A1-S', type: 'rifle', damage: 38, fireRateRPM: 600,
    magazine: 20, reserveAmmo: 40, reloadTimeMs: 3100, price: 2900,
    moveSpeed: 5.625, baseSpread: 0.15, maxSpread: 4.0, spreadPerShot: 0.25,
    killReward: 300, penetration: 0.70, silenced: true,
  },
  knife: {
    name: 'Knife', type: 'melee', damage: 40, fireRateRPM: 120,
    magazine: 1, reserveAmmo: 0, reloadTimeMs: 0, price: 0,
    moveSpeed: 6.5, baseSpread: 0.0, maxSpread: 0.0, spreadPerShot: 0.0,
    killReward: 1500, penetration: 1.0,
  },
};
